/**
 * Create-profile form shell.
 *
 * Takes a label, creates a profile, and shows the one-time phrase and PIN. In the
 * prototype these are cosmetic (see the mock service); in Phase 3 they become
 * real, generated server-side and shown exactly once. The UI habit built here —
 * show once, offer to print, then move on — is the one the real flow needs.
 *
 * The phrase and PIN are rendered into the page for the owner to record. Neither
 * is placed in this page's URL, and the print action uses the browser's own
 * print rather than a URL carrying the values. The card's QR code does encode the
 * phrase — see `src/ui/phrase.js` for why that is acceptable and the PIN is not.
 */

import { el, icon, fieldValue } from "../ui/dom.js";
import { pageHeader, errorAlert } from "../ui/patterns.js";
import { renderCredentialsPanel } from "../ui/credential-panel.js";

/** @type {import("../app/router.js").PageRenderer} */
export default function renderCreateProfile(context) {
  const frag = document.createDocumentFragment();
  frag.append(
    pageHeader(
      "Create a profile",
      "Give the profile a name only you need to understand, such as “Mum's people”.",
    ),
  );

  const outcome = el("div", { class: "kin-stack", "aria-live": "polite" });

  const labelField = el("wa-input", {
    label: "Profile name",
    name: "label",
    autocomplete: "off",
    required: true,
  });

  const submit = el("wa-button", { type: "submit", variant: "brand" }, [
    icon("plus"),
    "Create profile",
  ]);

  const form = el(
    "form",
    {
      class: "kin-stack kin-form",
      novalidate: true,
      on: {
        submit: async (event) => {
          event.preventDefault();
          outcome.replaceChildren();
          const label = fieldValue(labelField);
          if (!label) {
            outcome.replaceChildren(errorAlert("Give the profile a name to continue."));
            return;
          }
          submit.setAttribute("loading", "");
          try {
            const profile = await context.services.profiles.create(label);
            form.hidden = true;
            outcome.replaceChildren(renderCredentials(context, profile));
          } catch (error) {
            // Server-side failures reach here as plain-language messages (§9):
            // a plan limit, an unverified email, a transport problem. Showing the
            // message keeps the form filled in so the attempt is not lost.
            outcome.replaceChildren(
              errorAlert(error instanceof Error ? error.message : "That did not work."),
            );
          } finally {
            submit.removeAttribute("loading");
          }
        },
      },
    },
    [labelField, submit],
  );

  frag.append(form, outcome);
  return frag;
}

/**
 * The one-time credentials panel.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").RecoveryProfile} profile
 * @returns {DocumentFragment}
 */
function renderCredentials(context, profile) {
  return renderCredentialsPanel({
    heading: `“${profile.label}” is ready.`,
    label: profile.label,
    phrase: profile.phrase,
    pin: profile.pin,
    intro:
      "This is the only time KinCode will show these. Write them down or print " +
      "them, and give them to the people who may need to recover your contacts.",
    pinNote: "You can change this PIN later from Replace credentials on your dashboard.",
    actions: [
      el(
        "wa-button",
        {
          variant: "brand",
          on: { click: () => context.navigate("/profiles/edit", { profileId: profile.id }) },
        },
        [icon("arrow-right"), "Add contacts"],
      ),
    ],
  });
}
